import * as React from 'react';
import colors from '../../../../../../config/theme/colors';
import { fm } from 'Components/FormatMessage';
import InputHandler from 'Components/InputHandler';
import type { IExtendedSearchField } from '../ExtendedSearchField';
import Unit from 'Components/Unit';
// import { serologicalProfileFieldToUnitMap } from '../../config/mgravis';

const SerologicalProfileField = ({
  searchFields,
  logicalOperators,
  onChange,
  onChangeLogOp,
}: IOwnProps): React.JSX.Element => {
  const thisSearchField =
    searchFields.serologicalProfile !== 'noCropping' ? searchFields.serologicalProfile : undefined;

  // const fieldToUnitMap = serologicalProfileFieldToUnitMap;

  const handleChange =
    (parentField: 'S-AChR-Ab' | 'S-MuSKAb') =>
    (values: TOnChangeValues): void => {
      const thisName = Object.keys(values)[0];
      if (!thisName) return;
      const thisValue = values[thisName];
      const baseValue: { [key: string]: unknown } = typeof thisSearchField === 'object' ? { ...thisSearchField } : {};

      if (typeof thisValue === 'boolean') {
        // Checkbox: checked --> 'noCropping' (enabled, no range constraint), unchecked --> undefined
        const fieldValue = thisValue ? 'noCropping' : undefined;
        const updated = { ...baseValue, [parentField]: fieldValue };
        const newValues: { [key: string]: unknown } | 'noCropping' =
          !updated['S-AChR-Ab'] && !updated['S-MuSKAb'] ? 'noCropping' : updated;
        onChange({ serologicalProfile: newValues });
      } else {
        // NumberField: update minValue/maxValue within the parent field
        const currentParentValue =
          typeof thisSearchField === 'object' && thisSearchField[parentField] !== 'noCropping'
            ? (thisSearchField[parentField] as { minValue?: number; maxValue?: number })
            : { minValue: undefined, maxValue: undefined };
        const newParentValue = {
          ...currentParentValue,
          [thisName]: typeof thisValue === 'number' ? thisValue : undefined,
        };
        const fieldValue =
          newParentValue.minValue === undefined && newParentValue.maxValue === undefined
            ? 'noCropping'
            : newParentValue;
        onChange({ serologicalProfile: { ...baseValue, [parentField]: fieldValue } });
      }
    };

  return (
    <React.Fragment>
      {(['S-AChR-Ab', 'S-MuSKAb'] as ('S-AChR-Ab' | 'S-MuSKAb')[]).map((f, i, arr) => {
        const v = thisSearchField?.[f];
        // const unit = fieldToUnitMap.get(f);
        // TODO: Units left out for now "to be safe" -- remove entirely if it's decided for good that they won't be needed at all
        const unit = undefined;
        return (
          <React.Fragment key={`${f}.${i}`}>
            <div
              style={{
                backgroundColor: colors.lightestGray,
                padding: '1rem',
                marginBottom: i === arr.length - 1 ? '1rem' : undefined,
              }}
            >
              <div style={{ marginBottom: '1rem', color: colors.tertiaryText }}>
                {fm('extendedSearch.searchCriteriaTitle')}
                {` ${i + 1}`}
              </div>
              <InputHandler
                type="CheckboxSingle"
                editing={true}
                name={f}
                formData={{
                  onChange: handleChange(f),
                  document: {
                    [f]: thisSearchField?.[f] !== undefined ? true : undefined,
                  },
                }}
                option="true"
                optionFormatter={(): string | React.JSX.Element => f}
                showLabel
              />
              {typeof thisSearchField === 'object' && (
                <React.Fragment>
                  <div style={{ marginBottom: '1rem', color: colors.tertiaryText }}>{fm('extendedSearch.result')}</div>
                  <Unit unit={unit}>
                    <React.Fragment>
                      {['minValue', 'maxValue'].map((ff, ii) => {
                        const vv = v && typeof v === 'object' ? v[ff as keyof typeof v] : undefined;
                        return (
                          <React.Fragment key={`${ff}-${ii}`}>
                            <InputHandler
                              type="NumberField"
                              editing={true}
                              name={ff}
                              formData={{
                                onChange: handleChange(f),
                                document: { [ff]: vv },
                              }}
                              min={0}
                              placeholder={`extendedSearch.${{ 0: 'min', 1: 'max' }[ii]}`}
                              width={7}
                              precision={2}
                              disabled={typeof thisSearchField !== 'object' || !thisSearchField[f]}
                            />
                            {ii === 0 && ' - '}
                          </React.Fragment>
                        );
                      })}
                    </React.Fragment>
                  </Unit>
                </React.Fragment>
              )}
            </div>
            {i === 0 && (
              <div
                style={{
                  padding: '1rem',
                  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                  visibility: !(thisSearchField?.['S-AChR-Ab'] && thisSearchField['S-MuSKAb']) ? 'hidden' : undefined,
                }}
              >
                <InputHandler
                  type="Select"
                  editing={true}
                  name="serologicalProfileLogicalOperator"
                  formData={{
                    onChange: (values: TOnChangeValues) => {
                      const key = Object.keys(values)[0];
                      if (!key) return;
                      onChangeLogOp({ serologicalProfile: values[key] });
                    },
                    document: { serologicalProfileLogicalOperator: logicalOperators['serologicalProfile'] },
                  }}
                  options={['AND', 'OR']}
                  optionFormatter={(name: string | number): string | React.JSX.Element => (
                    <span style={{ textTransform: 'uppercase' }}>{fm(`general.${name.toString().toLowerCase()}`)}</span>
                  )}
                  minWidth={6}
                  fittedWidth
                />
              </div>
            )}
          </React.Fragment>
        );
      })}
    </React.Fragment>
  );
};

interface IOwnProps extends Pick<
  IExtendedSearchField,
  'searchFields' | 'logicalOperators' | 'onChange' | 'onChangeLogOp'
> {}

export default SerologicalProfileField;
